import PropTypes from 'prop-types';
import { IoTimeOutline } from 'react-icons/io5';
import BibleProgressCalendar from '../progress/BibleProgressCalendar';
import ChallengeGoals from '../progress/ChallengeGoals';
import {
  getChallengeGoals,
  getReadersToday,
  groupChaptersByBook,
  neededChapterIdSet,
  shouldEnumerateStillNeeded
} from '../../utils/churchChallenge';
import './ChurchChallengeSection.css';

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

/**
 * Church-scoped challenge map, still-needed list, and (when the API sends
 * them) season goals. Used by the member Church page and the admin
 * Church Management page. Numbers come from getChallengeStats — do not
 * recompute on the client.
 */
const ChurchChallengeSection = ({ challenge, church, stats, payload = null }) => {
  if (!stats) return null;

  const goals = getChallengeGoals(stats);
  const readersToday = getReadersToday(payload, stats);
  const neededIds = neededChapterIdSet(stats.chaptersNeeded);
  const neededGroups = groupChaptersByBook(stats.chaptersNeeded);
  const enumerateNeeded = shouldEnumerateStillNeeded(
    stats.chaptersNeeded,
    stats.totalBibleChapters
  );
  const remaining = stats.chaptersToNextCompletion ?? 0;
  const completions = stats.completeBibleReads ?? 0;
  const churchName = church?.name || 'Our church';

  return (
    <section className="church-challenge-section">
      <header className="church-challenge-hero">
        {challenge && (
          <div className="church-challenge-meta">
            <span className="church-challenge-name">{challenge.name}</span>
            {typeof challenge.daysRemaining === 'number' && (
              <span className="church-challenge-countdown">
                <IoTimeOutline size={14} aria-hidden="true" />
                {challenge.daysRemaining === 0
                  ? 'Last day'
                  : `${challenge.daysRemaining} day${challenge.daysRemaining === 1 ? '' : 's'} left`}
              </span>
            )}
          </div>
        )}

        <h2 className="church-challenge-headline">
          {churchName} has read the Bible through{' '}
          <strong className="church-challenge-number">{formatNumber(completions)}</strong>{' '}
          {completions === 1 ? 'time' : 'times'}.
        </h2>

        <p className="church-challenge-subline">
          <strong>{formatNumber(remaining)}</strong>{' '}
          {remaining === 1 ? 'chapter' : 'chapters'} to our next completion.
        </p>

        {readersToday !== null && (
          <p className="church-challenge-readers-today">
            {formatNumber(readersToday)}{' '}
            {readersToday === 1 ? 'person' : 'people'} from {churchName}{' '}
            {readersToday === 1 ? 'has' : 'have'} read today.
          </p>
        )}
      </header>

      {goals.length > 0 && (
        <ChallengeGoals
          goals={goals}
          challengeName={challenge?.name}
        />
      )}

      <div className="church-still-needed">
        <div className="church-still-needed-header">
          <h3>Still needed</h3>
          <p>
            {neededGroups.length === 0
              ? 'No gap list yet — the map below is the source of truth.'
              : enumerateNeeded
                ? `${formatNumber(remaining)} chapter${remaining === 1 ? '' : 's'} your church still needs for the next read-through. Rally readers to these gaps.`
                : `${formatNumber(remaining)} chapters still sit at the minimum. The map below is the full list; books with the most gaps are named here so you can rally readers.`}
          </p>
        </div>

        {neededGroups.length > 0 && (
          <div className="church-still-needed-books">
            {neededGroups.map(group => (
              <div key={group.book} className="church-still-needed-book">
                <h4>
                  {group.book}
                  {!enumerateNeeded && (
                    <span className="church-still-needed-count">
                      {' '}{group.chapters.length} chapter{group.chapters.length === 1 ? '' : 's'}
                    </span>
                  )}
                </h4>
                {enumerateNeeded && (
                  <ul>
                    {group.chapters.map(chapter => (
                      <li key={chapter.id}>
                        <span className="church-still-needed-chip">
                          {chapter.chapter}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="church-challenge-map">
        <div className="church-challenge-map-header">
          <h3>{churchName}&apos;s Bible</h3>
          <p>
            Only readings by {churchName} during
            {challenge
              ? ` ${challenge.name}`
              : ' the active challenge'}
            {challenge?.startDate && challenge?.endDate
              ? ` (${challenge.startDate} – ${challenge.endDate})`
              : ''}
            . Reading outside that window does not appear here.
          </p>
        </div>

        {stats.chapters && stats.chapterReadCounts && (
          <BibleProgressCalendar
            chapters={stats.chapters}
            chapterReadCounts={stats.chapterReadCounts}
            neededChapterIds={neededIds}
          />
        )}
      </div>
    </section>
  );
};

ChurchChallengeSection.propTypes = {
  challenge: PropTypes.shape({
    name: PropTypes.string,
    daysRemaining: PropTypes.number,
    startDate: PropTypes.string,
    endDate: PropTypes.string
  }),
  church: PropTypes.shape({
    name: PropTypes.string
  }),
  stats: PropTypes.shape({
    completeBibleReads: PropTypes.number,
    chaptersToNextCompletion: PropTypes.number,
    chaptersNeeded: PropTypes.array,
    totalBibleChapters: PropTypes.number,
    chapters: PropTypes.array,
    chapterReadCounts: PropTypes.object,
    goals: PropTypes.array,
    readersToday: PropTypes.number
  }),
  payload: PropTypes.object
};

export default ChurchChallengeSection;
